import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { useMediaDevices } from '@/hooks/useMediaDevices';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { buildCameraPreviewConstraints } from '@/lib/cameraCapture';

export interface MediaDeviceSelection {
  readonly cameraId: string;
  readonly microphoneId: string;
  readonly audioOutputId: string;
  readonly cameraEnabled: boolean;
  readonly microphoneEnabled: boolean;
}

interface MediaDeviceTesterProps {
  readonly theme?: 'LIGHT' | 'DARK';
  readonly cameraEnabled: boolean;
  readonly microphoneEnabled: boolean;
  readonly setCameraEnabled: Dispatch<SetStateAction<boolean>>;
  readonly setMicrophoneEnabled: Dispatch<SetStateAction<boolean>>;
  /** Rapporte les choix de périphériques au parent (single source of truth pour le live) */
  readonly onDevicesChange?: (devices: MediaDeviceSelection) => void;
}

interface WaveformVisualizerProps {
  readonly frequencyData: Uint8Array;
  readonly theme?: 'LIGHT' | 'DARK';
}

function WaveformVisualizer({ frequencyData, theme = 'DARK' }: WaveformVisualizerProps) {
  const barCount = 60;
  const maxHeight = 100;
  if (frequencyData.length === 0) {
    return (
      <div className="w-full flex items-center justify-center gap-0.5 h-12 px-2  overflow-hidden">
        {Array.from({ length: barCount }, (_, i) => i).map((index) => (
          <div
            key={`empty-bar-${index}`}
            className="w-full bg-gray-300 rounded-full transition-all duration-75"
            style={{ height: '2px' }}
          />
        ))}
      </div>
    );
  }
  const globalAverage = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length;
  const normalizedGlobalAverage = Math.min(globalAverage / 255, 1);
  const bars: number[] = [];

  const getWindowWeight = (index: number, total: number): number => {
    const normalizedPosition = (index / (total - 1)) * 2 - 1;
    const sigma = 0.4;
    const weight = Math.exp(-(normalizedPosition * normalizedPosition) / (2 * sigma * sigma));
    return weight;
  };

  for (let i = 0; i < barCount; i++) {
    const windowWeight = getWindowWeight(i, barCount);
    const weightedValue = normalizedGlobalAverage * windowWeight;

    const curved = Math.pow(weightedValue, 0.6);
    bars.push(curved);
  }

  return (
    <div className="w-full flex items-center justify-between gap-0.5 h-12 px-2 overflow-hidden">
      {bars.map((value, index) => {
        const height = Math.max(value * maxHeight, 2);
        return (
          <div
            key={`bar-${index}-${value.toFixed(3)}`}
            className={cn(
              'w-[3px] rounded-full transition-all duration-75',
              theme === 'DARK' ? 'bg-foreground' : 'bg-black',
            )}
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}

export function MediaDeviceTester({
  theme = 'DARK',
  cameraEnabled,
  microphoneEnabled,
  setCameraEnabled,
  setMicrophoneEnabled,
  onDevicesChange,
}: MediaDeviceTesterProps) {
  const { t } = useWebinarT('webinars');
  const { audioInputs, videoInputs, audioOutputs, loading } = useMediaDevices();
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>('');
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>('');
  const [frequencyData, setFrequencyData] = useState<Uint8Array>(new Uint8Array(0));

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (videoInputs.length > 0 && !selectedCameraId) {
      setSelectedCameraId(videoInputs[0].deviceId);
    }
    if (audioInputs.length > 0 && !selectedMicrophoneId) {
      setSelectedMicrophoneId(audioInputs[0].deviceId);
    }
    if (audioOutputs.length > 0 && !selectedAudioOutputId) {
      setSelectedAudioOutputId(audioOutputs[0].deviceId);
    }
  }, [
    videoInputs,
    audioInputs,
    audioOutputs,
    selectedCameraId,
    selectedMicrophoneId,
    selectedAudioOutputId,
  ]);

  useEffect(() => {
    if (onDevicesChange && selectedCameraId && selectedMicrophoneId) {
      onDevicesChange({
        cameraId: selectedCameraId,
        microphoneId: selectedMicrophoneId,
        audioOutputId: selectedAudioOutputId,
        cameraEnabled,
        microphoneEnabled,
      });
    }
  }, [
    onDevicesChange,
    selectedCameraId,
    selectedMicrophoneId,
    selectedAudioOutputId,
    cameraEnabled,
    microphoneEnabled,
  ]);

  useEffect(() => {
    if (!cameraEnabled || !selectedCameraId) {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: buildCameraPreviewConstraints(selectedCameraId),
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors de l'activation de la caméra:", error);
          setCameraEnabled(false);
        }
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [cameraEnabled, selectedCameraId, setCameraEnabled]);

  useEffect(() => {
    if (!microphoneEnabled || !selectedMicrophoneId) {
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
        microphoneStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setFrequencyData(new Uint8Array(0));
      return;
    }

    let cancelled = false;

    const startMicrophone = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: selectedMicrophoneId } },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        microphoneStreamRef.current = stream;

        const audioContext = new (
          globalThis.AudioContext || (globalThis as any).webkitAudioContext
        )();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        analyser.fftSize = 256;
        microphone.connect(analyser);

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          void audioContext.close();
          return;
        }

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateAudioLevel = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          setFrequencyData(new Uint8Array(dataArray));
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors de l'activation du microphone:", error);
          setMicrophoneEnabled(false);
        }
      }
    };

    void startMicrophone();

    return () => {
      cancelled = true;
      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
        microphoneStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setFrequencyData(new Uint8Array(0));
    };
  }, [microphoneEnabled, selectedMicrophoneId, setMicrophoneEnabled]);

  const toggleCamera = () => {
    setCameraEnabled((c) => !c);
  };

  const handleCameraChange = (deviceId: string) => {
    const wasEnabled = cameraEnabled;
    setCameraEnabled(false);
    setSelectedCameraId(deviceId);
    setTimeout(() => {
      if (wasEnabled) {
        setCameraEnabled(true);
      }
    }, 100);
  };

  const handleMicrophoneChange = (deviceId: string) => {
    const wasEnabled = microphoneEnabled;
    setMicrophoneEnabled(false);
    setSelectedMicrophoneId(deviceId);
    setTimeout(() => {
      if (wasEnabled) {
        setMicrophoneEnabled(true);
      }
    }, 100);
  };

  const handleAudioOutputChange = async (deviceId: string) => {
    setSelectedAudioOutputId(deviceId);
    if (videoRef.current && 'setSinkId' in videoRef.current) {
      try {
        await (
          videoRef.current as HTMLVideoElement & { setSinkId: (id: string) => Promise<void> }
        ).setSinkId(deviceId);
      } catch (error) {
        console.error('Erreur lors du changement de périphérique de sortie audio:', error);
      }
    }
  };

  useEffect(() => {
    const applyAudioOutput = async () => {
      if (!selectedAudioOutputId) return;

      if (videoRef.current && 'setSinkId' in videoRef.current) {
        try {
          await (
            videoRef.current as HTMLVideoElement & { setSinkId: (id: string) => Promise<void> }
          ).setSinkId(selectedAudioOutputId);
        } catch (error) {
          console.error(
            "Erreur lors de l'application du périphérique de sortie audio sur la vidéo:",
            error,
          );
        }
      }
    };

    applyAudioOutput();
  }, [selectedAudioOutputId]);

  return (
    <div className="space-y-3 md:space-y-6">
      <div className="space-y-3 md:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-2">
          <Select value={selectedCameraId} disabled={loading} onValueChange={handleCameraChange}>
            <SelectTrigger
              label={t('adminLive.mediaDeviceTester.videoLabel')}
              classLabel="text-xs md:text-sm"
              className="h-12 md:h-16 col-span-1 truncate"
            >
              <SelectValue
                placeholder={t('adminLive.mediaDeviceTester.selectCamera')}
                className="truncate"
              >
                {selectedCameraId
                  ? videoInputs.find((d) => d.deviceId === selectedCameraId)?.label ||
                    t('adminLive.mediaDeviceTester.cameraFallback', {
                      index: videoInputs.findIndex((d) => d.deviceId === selectedCameraId) + 1,
                    })
                  : t('adminLive.mediaDeviceTester.selectCamera')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {videoInputs.map((device) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label ||
                    t('adminLive.mediaDeviceTester.cameraFallback', {
                      index: videoInputs.indexOf(device) + 1,
                    })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedMicrophoneId}
            disabled={loading}
            onValueChange={handleMicrophoneChange}
          >
            <SelectTrigger
              label={t('adminLive.mediaDeviceTester.microphoneLabel')}
              classLabel="text-xs md:text-sm"
              className="h-12 md:h-16 col-span-1 truncate"
            >
              <SelectValue
                placeholder={t('adminLive.mediaDeviceTester.selectMicrophone')}
                className="truncate"
              >
                {selectedMicrophoneId
                  ? audioInputs.find((d) => d.deviceId === selectedMicrophoneId)?.label ||
                    t('adminLive.mediaDeviceTester.microphoneFallback', {
                      index: audioInputs.findIndex((d) => d.deviceId === selectedMicrophoneId) + 1,
                    })
                  : t('adminLive.mediaDeviceTester.selectMicrophone')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {audioInputs.map((device) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label ||
                    t('adminLive.mediaDeviceTester.microphoneFallback', {
                      index: audioInputs.indexOf(device) + 1,
                    })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedAudioOutputId}
            disabled={loading}
            onValueChange={handleAudioOutputChange}
          >
            <SelectTrigger
              label={t('adminLive.mediaDeviceTester.audioOutputLabel')}
              classLabel="text-xs md:text-sm"
              className="h-12 md:h-16 col-span-1 truncate"
            >
              <SelectValue
                placeholder={t('adminLive.mediaDeviceTester.selectAudioOutput')}
                className="truncate"
              >
                {selectedAudioOutputId
                  ? audioOutputs.find((d) => d.deviceId === selectedAudioOutputId)?.label ||
                    t('adminLive.mediaDeviceTester.audioOutputFallback', {
                      index:
                        audioOutputs.findIndex((d) => d.deviceId === selectedAudioOutputId) + 1,
                    })
                  : t('adminLive.mediaDeviceTester.selectAudioOutput')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {audioOutputs.map((device) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label ||
                    t('adminLive.mediaDeviceTester.audioOutputFallback', {
                      index: audioOutputs.indexOf(device) + 1,
                    })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <div className="relative w-full aspect-video max-h-[364px] max-md:mx-auto max-md:h-[22svh] max-md:w-auto bg-black rounded-2xl border border-card overflow-hidden group">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {!cameraEnabled && (
              <div className="absolute top-0 left-0 w-full h-full bg-[#666] flex items-center justify-center">
                <p className="text-white text-sm flex items-center gap-2">
                  <VideoOff className="size-24" />
                </p>
              </div>
            )}
            <div className="absolute top-0 flex gap-2 right-0 p-4 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-300">
              <Button
                onClick={toggleCamera}
                variant={cameraEnabled ? 'default' : 'secondary'}
                size="icon-lg"
                disabled={loading}
              >
                {cameraEnabled ? <Video className="size-4 " /> : <VideoOff className="size-4" />}
              </Button>
              <Button
                onClick={() =>
                  microphoneEnabled ? setMicrophoneEnabled(false) : setMicrophoneEnabled(true)
                }
                variant={microphoneEnabled ? 'default' : 'secondary'}
                size="icon-lg"
                disabled={loading}
              >
                {microphoneEnabled ? <Mic className="size-4" /> : <MicOff className="size-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <WaveformVisualizer frequencyData={frequencyData} theme={theme} />
      </div>
    </div>
  );
}
